import React, { useState } from 'react';
import { Box, Help, StringHelpers, SystemText } from '@salesperformanceint/e4shared';
import IntegrationOvertime from './components/overTime';
import CompetencyList from 'components/general/competencyListNoRoute';
import MetricsBreakdown from './components/breakdown';
import PeriodSelector from '../periodSelector';
import { useSelector } from 'react-redux';
import RealtimeMetrics from '../realtimeMetrics';
import Rater from '../competencies/components/rater';
import DataRow from '../dataRow';
import Details from './components/details';
import { e4Format, e4Formats } from 'helpers/dates';
import posthog from 'posthog-js';

export const IntegrationMetrics = ({
    testId,
    integration,
    profile,
    metrics,
    loading,
    lastUpdated,
    extraButtons,
    setCompetencies,
    updateRange,
    updateOffset,
    extraMetrics,
    filteredMetric,
    setFilteredMetric,
    realtimeMetrics,
    currentMetric,
}) => {
    const [show, setShow] = useState(false);
    const [showDetails, setShowDetails] = useState(false);
    const [metric, setMetric] = useState(null);
    const [range, setRange] = React.useState('month');
    const [offset, setOffset] = React.useState(0);
    const [showBreakdown, setShowBreakdown] = React.useState(false);

    const user = useSelector(state => state.user.user);

    if ((metrics || []).concat(extraMetrics || []).length === 0) return null;

    React.useEffect(() => {
        updateRange(range);
    }, [range]);

    React.useEffect(() => {
        updateOffset(offset);
    }, [offset]);

    const hideWeekly = ['GONG', 'LKIN'].some(x => x === currentMetric);

    const buttons = () => (
        <PeriodSelector
            data-testid="periodselector-integration-metrics"
            hideWeekly={hideWeekly}
            range={range}
            setRange={setRange}
            offset={offset}
            setOffset={setOffset}
        />
    );

    return (
        <div data-testid={testId} className="metric121">
            <Box data-testid="user121-metrics-box" title="Metrics" button={buttons} preButton={extraButtons}>
                <div>
                    {metrics?.map((m, i) => {
                        const title = `${m.type === 'M' ? SystemText('User121/CrmMetrics/StaffAveragePrefix') : ''}${
                            m.name
                        }`;

                        const help = (
                            <Help id={`metric-help-${m.id}`}>
                                {m.description.replace(/(\r\n|\n|\r)/gm, '') && <div className="mb10">{m.description}</div>}
                                <CompetencyList competencies={m.competencies} />
                            </Help>
                        );
                        const special = (
                            <span className="ml10 mr10" style={{ position: 'relative', top: '-5px' }}>
                                <Rater
                                    hideOutOf
                                    benchmark={m.percentile}
                                    loading={loading}
                                    level={StringHelpers.formatMetric(m.format, m.value, user.currencySymbol)}
                                />
                            </span>
                        );

                        const showSecondBtn =
                            m.format !== 'P' &&
                            m.type !== 'M' &&
                            !m.calc_Days &&
                            !m.calc_Avg &&
                            m.extraFields &&
                            m.extraFields.length > 0;

                        const buttons = (
                            <>
                                <button
                                    data-testid="crmmetric-overtime-button"
                                    className="btn btn-xs btn-info"
                                    title={SystemText('User121/CrmMetrics/Buttons/Overtime/Hover')}
                                    onClick={() => {
                                        setShow(true);
                                        setMetric(m);
                                        posthog.capture('show-metric-over-time', {
                                            integration: m.ref,
                                            metric: m.name,
                                        });
                                    }}
                                >
                                    <i className="fa fa-line-chart"></i>
                                </button>

                                {m.ref === 'e4' ? (
                                    <button
                                        className="mr10 btn btn-xs btn-info"
                                        title={SystemText(
                                            `User121/e4Metrics/Buttons/Details/Hover${
                                                m.property === 'e4Points' ? '/E4Points' : ''
                                            }`
                                        )}
                                        data-testid="e4metrics-buttons-details"
                                        onClick={() => {
                                            setShowDetails(true);
                                            setMetric(m);
                                        }}
                                        disabled={
                                            ['e4Points'].some(x => x === m.property) ||
                                            (!m.value && !['BadgesWon'].some(x => x === m.property))
                                        }
                                    >
                                        <i className="fa fa-binoculars"></i>
                                    </button>
                                ) : (
                                    <button
                                        data-testid="crmmetric-breakdown-button"
                                        className="btn btn-xs btn-info"
                                        title={SystemText('User121/CrmMetrics/Buttons/Breakdown/Hover')}
                                        disabled={!m.value || !showSecondBtn}
                                        onClick={() => {
                                            setShowBreakdown(true);
                                            setMetric(m);
                                        }}
                                    >
                                        <i className="fa fa-pie-chart" />
                                    </button>
                                )}

                                <button
                                    data-testid="crmmetric-filter-button"
                                    className={`${
                                        filteredMetric === `M_${m.type}_${m.id}` ? 'btn-selected' : ''
                                    } btn btn-xs btn-info`}
                                    title={SystemText('User121/CrmMetrics/Buttons/Filter/Hover')}
                                    onClick={() => {
                                        setCompetencies(m.competencies);
                                        setFilteredMetric(`M_${m.type}_${m.id}`);
                                        posthog.capture('filter-capabilities-by-metric', {
                                            integration: m.ref,
                                            metric: m.name,
                                        });
                                    }}
                                >
                                    <i className="fa fa-play" />
                                </button>
                            </>
                        );

                        return (
                            <DataRow
                                key={`intmetric-row-${m.id}-${i}`}
                                prefix={null}
                                title={title}
                                help={help}
                                special={special}
                                buttons={buttons}
                                testPrefix="metric"
                            />
                        );
                    })}
                    <div className="col-xs-12 e4-data-row-container pt10">
                        <RealtimeMetrics
                            profile={profile}
                            realtimeMetrics={realtimeMetrics}
                            extraMetrics={extraMetrics}
                            integration={integration}
                        />
                    </div>
                </div>
                {lastUpdated && <div className="mt10 text-sm text-center">Last Updated {e4Format(lastUpdated)}</div>}
                <IntegrationOvertime
                    profile={profile}
                    metric={metric}
                    show={show}
                    range={range}
                    close={() => setShow(false)}
                />
            </Box>
            <MetricsBreakdown
                profile={profile}
                metric={showBreakdown ? metric : null}
                show={showBreakdown}
                range={range}
                offset={offset}
                close={() => {
                    setShowBreakdown(false);
                    setMetric(null);
                }}
            />
            <Details
                profile={profile}
                metric={showDetails ? metric : null}
                show={showDetails}
                range={range}
                offset={offset}
                close={() => {
                    setShowDetails(false);
                    setMetric(null);
                }}
            />
        </div>
    );
};

export default IntegrationMetrics;
